import fs from 'fs-extra';
import path from 'path';
import { WORKFLOW_DIR, WORKFLOWS_DIR } from '../../constants.js';

export interface FlowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: any;
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  type?: string;
  animated?: boolean;
  style?: any;
}

export interface Flow {
  id: string;
  name: string;
  description?: string;
  nodes: FlowNode[];
  edges: FlowEdge[];
  viewport?: {
    x: number;
    y: number;
    zoom: number;
  };
  created: string;
  updated: string;
}

export class FlowService {
  private flowsDir: string;

  constructor() {
    this.flowsDir = path.join(process.cwd(), WORKFLOW_DIR, WORKFLOWS_DIR);
    this.ensureFlowsDir();
  }

  private async ensureFlowsDir() {
    await fs.ensureDir(this.flowsDir);
    
    // 创建示例流程图
    const sampleFlowPath = path.join(this.flowsDir, 'sample-flow.json');
    if (!(await fs.pathExists(sampleFlowPath))) {
      const sampleFlow: Flow = {
        id: 'sample-flow',
        name: '示例工作流',
        description: '这是一个示例工作流，展示了任务之间的依赖关系',
        nodes: [
          {
            id: '1',
            type: 'default',
            position: { x: 100, y: 100 },
            data: { label: '需求分析', status: 'done' }
          },
          {
            id: '2',
            type: 'default',
            position: { x: 300, y: 100 },
            data: { label: '设计方案', status: 'in_progress' }
          },
          {
            id: '3',
            type: 'default',
            position: { x: 500, y: 100 },
            data: { label: '开发实现', status: 'todo' }
          },
          {
            id: '4',
            type: 'default',
            position: { x: 700, y: 100 },
            data: { label: '测试验证', status: 'todo' }
          }
        ],
        edges: [
          {
            id: 'e1-2',
            source: '1',
            target: '2',
            animated: true
          },
          {
            id: 'e2-3',
            source: '2',
            target: '3'
          },
          {
            id: 'e3-4',
            source: '3',
            target: '4'
          }
        ],
        viewport: {
          x: 0,
          y: 0,
          zoom: 1
        },
        created: new Date().toISOString(),
        updated: new Date().toISOString()
      };
      
      await fs.writeJson(sampleFlowPath, sampleFlow, { spaces: 2 });
    }
  }

  /**
   * 获取所有流程图
   */
  async list(): Promise<Flow[]> {
    try {
      const files = await fs.readdir(this.flowsDir);
      const flows: Flow[] = [];
      
      for (const file of files) {
        if (file.endsWith('.json')) {
          try {
            const flow = await fs.readJson(path.join(this.flowsDir, file));
            flows.push(flow);
          } catch (error) {
            console.error(`Error reading flow file ${file}:`, error);
          }
        }
      }
      
      return flows;
    } catch (error) {
      console.error('Error listing flows:', error);
      return [];
    }
  }

  /**
   * 获取单个流程图
   */
  async get(id: string): Promise<Flow | null> {
    try {
      const flowPath = path.join(this.flowsDir, `${id}.json`);
      if (await fs.pathExists(flowPath)) {
        return await fs.readJson(flowPath);
      }
      return null;
    } catch (error) {
      console.error('Error reading flow:', error);
      return null;
    }
  }

  /**
   * 保存流程图
   */
  async save(id: string, data: { nodes: FlowNode[]; edges: FlowEdge[]; viewport?: any }): Promise<Flow> {
    const flowPath = path.join(this.flowsDir, `${id}.json`);
    
    // 获取现有流程图或创建新的
    let flow: Flow;
    const existingFlow = await this.get(id);
    
    if (existingFlow) {
      // 更新现有流程图
      flow = {
        ...existingFlow,
        nodes: data.nodes,
        edges: data.edges,
        viewport: data.viewport || existingFlow.viewport,
        updated: new Date().toISOString()
      };
    } else {
      // 创建新流程图
      flow = {
        id,
        name: `工作流 ${id}`,
        nodes: data.nodes,
        edges: data.edges,
        viewport: data.viewport || { x: 0, y: 0, zoom: 1 },
        created: new Date().toISOString(),
        updated: new Date().toISOString()
      };
    }
    
    // 保存到文件
    await fs.writeJson(flowPath, flow, { spaces: 2 });
    
    return flow;
  }

  /**
   * 删除流程图
   */
  async delete(id: string): Promise<void> {
    const flowPath = path.join(this.flowsDir, `${id}.json`);
    if (await fs.pathExists(flowPath)) {
      await fs.remove(flowPath);
    } else {
      throw new Error('Flow not found');
    }
  }

  /**
   * 更新流程图元数据
   */
  async updateMetadata(id: string, metadata: { name?: string; description?: string }): Promise<Flow> {
    const flow = await this.get(id);
    if (!flow) {
      throw new Error('Flow not found');
    }
    
    if (metadata.name !== undefined) flow.name = metadata.name;
    if (metadata.description !== undefined) flow.description = metadata.description;
    flow.updated = new Date().toISOString();
    
    const flowPath = path.join(this.flowsDir, `${id}.json`);
    await fs.writeJson(flowPath, flow, { spaces: 2 });
    
    return flow;
  }

  /**
   * 导出流程图为任务列表
   */
  async exportToTasks(id: string): Promise<any[]> {
    const flow = await this.get(id);
    if (!flow) {
      throw new Error('Flow not found');
    }
    
    // 将节点转换为任务格式
    const tasks = flow.nodes.map(node => ({
      id: `task-${node.id}`,
      name: node.data.label || `任务 ${node.id}`,
      status: node.data.status || 'todo',
      dependencies: flow.edges
        .filter(edge => edge.target === node.id)
        .map(edge => `task-${edge.source}`)
    }));
    
    return tasks;
  }
}